// Urlaub und Abwesenheit.
//
// Der Mitarbeiter beantragt, die Leitung entscheidet. Dass er sich nicht selbst
// genehmigen kann, ist keine Frage der Oberfläche — die Datenbank lässt ein
// Einfügen mit Status "genehmigt" gar nicht erst zu (Migration 031).

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/stores/authStore";

export type Abwesenheitsart = "urlaub" | "krank" | "unbezahlt" | "sonderurlaub";
export type AntragStatus = "beantragt" | "genehmigt" | "abgelehnt" | "zurueckgezogen";

export const ARTEN: { wert: Abwesenheitsart; label: string; hinweis: string }[] = [
  { wert: "urlaub", label: "Urlaub", hinweis: "Zählt vom Jahresanspruch ab" },
  { wert: "krank", label: "Krank", hinweis: "Keine Genehmigung nötig — nur Bescheid geben" },
  { wert: "sonderurlaub", label: "Sonderurlaub", hinweis: "Umzug, Hochzeit, Todesfall" },
  { wert: "unbezahlt", label: "Unbezahlt", hinweis: "Freistellung ohne Lohn" },
];

export interface Abwesenheit {
  id: string;
  profile_id: string;
  art: Abwesenheitsart;
  von: string;
  bis: string;
  halber_tag: boolean;
  arbeitstage: number;
  begruendung: string | null;
  status: AntragStatus;
  entschieden_von: string | null;
  entschieden_am: string | null;
  entscheidung_kommentar: string | null;
  created_at: string;
  person?: { full_name: string | null; urlaubstage_jahr: number } | null;
}

/**
 * Arbeitstage zwischen zwei Daten — Wochenenden zählen nicht.
 *
 * Feiertage bleiben bewusst außen vor: Sie hängen am Bundesland, ändern sich
 * jährlich, und eine falsche Liste wäre schlimmer als gar keine. Die Leitung
 * sieht den Zeitraum und korrigiert die Tage, wenn nötig.
 */
export function arbeitstage(von: string, bis: string, halberTag = false): number {
  const a = new Date(von + "T00:00:00");
  const b = new Date(bis + "T00:00:00");
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime()) || b < a) return 0;

  let tage = 0;
  for (const d = new Date(a); d <= b; d.setDate(d.getDate() + 1)) {
    const wt = d.getDay();
    if (wt !== 0 && wt !== 6) tage++;
  }
  return halberTag && tage === 1 ? 0.5 : tage;
}

/** Meine Anträge — und was davon geworden ist. */
export function useMeineAbwesenheiten() {
  const id = useAuth((s) => s.session?.user.id);
  return useQuery({
    queryKey: ["abwesenheiten", id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("absences")
        .select("*")
        .eq("profile_id", id!)
        .order("von", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as Abwesenheit[];
    },
  });
}

/** Was ist vom Jahresanspruch übrig? */
export function useUrlaubskonto() {
  const id = useAuth((s) => s.session?.user.id);

  return useQuery({
    queryKey: ["urlaubskonto", id],
    enabled: !!id,
    queryFn: async () => {
      const jahr = new Date().getFullYear();

      const [{ data: profil }, { data: urlaube }] = await Promise.all([
        supabase.from("profiles").select("urlaubstage_jahr").eq("id", id!).single(),
        supabase
          .from("absences")
          .select("arbeitstage, status")
          .eq("profile_id", id!)
          .eq("art", "urlaub")
          .gte("von", `${jahr}-01-01`)
          .lte("von", `${jahr}-12-31`),
      ]);

      const anspruch = Number(profil?.urlaubstage_jahr ?? 25);
      const liste = (urlaube ?? []) as { arbeitstage: number; status: AntragStatus }[];

      const genommen = liste
        .filter((u) => u.status === "genehmigt")
        .reduce((s, u) => s + Number(u.arbeitstage), 0);

      // Beantragtes ist noch nicht weg — aber man sollte es nicht zweimal verplanen.
      const beantragt = liste
        .filter((u) => u.status === "beantragt")
        .reduce((s, u) => s + Number(u.arbeitstage), 0);

      return { anspruch, genommen, beantragt, rest: anspruch - genommen - beantragt };
    },
  });
}

export function useUrlaubBeantragen() {
  const qc = useQueryClient();
  const id = useAuth((s) => s.session?.user.id);

  return useMutation({
    // Ohne "always" pausiert TanStack Query die Mutation, sobald der Browser
    // sich für offline hält: Die Funktion läuft nie, es gibt keinen Fehler, und
    // der Knopf tut wortlos nichts. So gingen die Stempel verloren — ein
    // Urlaubsantrag darf genauso wenig lautlos im Nichts landen.
    networkMode: "always",
    mutationFn: async (a: {
      art: Abwesenheitsart;
      von: string;
      bis: string;
      halber_tag: boolean;
      begruendung: string | null;
    }) => {
      const { error } = await supabase.from("absences").insert({
        profile_id: id!,
        ...a,
        arbeitstage: arbeitstage(a.von, a.bis, a.halber_tag),
        // Status bewusst NICHT mitgeschickt — der Standard ist "beantragt", und
        // die Datenbank lässt gar nichts anderes zu.
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["abwesenheiten"] });
      qc.invalidateQueries({ queryKey: ["urlaubskonto"] });
      qc.invalidateQueries({ queryKey: ["offene-antraege"] });
    },
  });
}

export function useAntragZurueckziehen() {
  const qc = useQueryClient();
  return useMutation({
    // Ohne "always" pausiert TanStack Query die Mutation, sobald der Browser
    // sich für offline hält: Die Funktion läuft nie, es gibt keinen Fehler, und
    // der Knopf tut wortlos nichts. So gingen die Stempel verloren — ein
    // Urlaubsantrag darf genauso wenig lautlos im Nichts landen.
    networkMode: "always",
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("absences")
        .update({ status: "zurueckgezogen" })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["abwesenheiten"] });
      qc.invalidateQueries({ queryKey: ["urlaubskonto"] });
      qc.invalidateQueries({ queryKey: ["offene-antraege"] });
    },
  });
}

// ═══════════════════════════════════════════════════ Leitung

/** Was liegt zur Entscheidung an? Das ist der Grund, warum Sebastian hier reinschaut. */
export function useOffeneAntraege() {
  return useQuery({
    queryKey: ["offene-antraege"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("absences")
        .select("*, person:profiles!absences_profile_id_fkey(full_name, urlaubstage_jahr)")
        .eq("status", "beantragt")
        .order("von");
      if (error) throw error;
      return (data ?? []) as unknown as Abwesenheit[];
    },
  });
}

export function useAlleAbwesenheiten(jahr: number) {
  return useQuery({
    queryKey: ["alle-abwesenheiten", jahr],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("absences")
        .select("*, person:profiles!absences_profile_id_fkey(full_name, urlaubstage_jahr)")
        .gte("von", `${jahr}-01-01`)
        .lte("von", `${jahr}-12-31`)
        .order("von", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as Abwesenheit[];
    },
  });
}

export function useEntscheiden() {
  const qc = useQueryClient();
  const id = useAuth((s) => s.session?.user.id);

  return useMutation({
    // Ohne "always" pausiert TanStack Query die Mutation, sobald der Browser
    // sich für offline hält: Die Funktion läuft nie, es gibt keinen Fehler, und
    // der Knopf tut wortlos nichts. So gingen die Stempel verloren — ein
    // Urlaubsantrag darf genauso wenig lautlos im Nichts landen.
    networkMode: "always",
    mutationFn: async (v: { id: string; genehmigt: boolean; kommentar?: string }) => {
      const { error } = await supabase
        .from("absences")
        .update({
          status: v.genehmigt ? "genehmigt" : "abgelehnt",
          entschieden_von: id,
          entschieden_am: new Date().toISOString(),
          entscheidung_kommentar: v.kommentar?.trim() || null,
        })
        .eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["offene-antraege"] });
      qc.invalidateQueries({ queryKey: ["alle-abwesenheiten"] });
      qc.invalidateQueries({ queryKey: ["abwesenheiten"] });
      qc.invalidateQueries({ queryKey: ["urlaubskonto"] });
    },
  });
}

export const artLabel = (a: Abwesenheitsart) => ARTEN.find((x) => x.wert === a)?.label ?? a;

export function zeitraum(von: string, bis: string, halberTag: boolean) {
  const f = (d: string) =>
    new Date(d + "T00:00:00").toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
  if (von === bis) return halberTag ? `${f(von)} (halber Tag)` : f(von);
  return `${f(von)} – ${f(bis)}`;
}
